import React, { FC, memo } from 'react'
import { RegionDirectoryPageTransport } from '../../transports/directory-transport'
import { PageProps, ResponseLocals } from '../../common/types'
import { GetServerSideProps } from '../../redux/types'
import { getLocaleFromLanguage, getMessagesFromLanguage } from '../../i18n/utils'
import { deleteSession } from '../../utils/delete-session'

import { getRoutesFromDomain } from '../../routes/utils'
import { BaseTransport, HeadTransport } from '../../transports'

import { useUserDetails } from '../../hooks/users'
import DirectoryPage from '../../components/directory-page'
import { pageClient } from '../../clients'
import config from '../../common/config'
import { useTranslation } from '../../hooks/locale'
import Page from '../../components/common/page'

type Props = {
    region: string
    data: BaseTransport<RegionDirectoryPageTransport>
    sitePathname: string
    fullUrl: string
    locale: string
} & PageProps

const DirectoryPageContainer: FC<Props> = ({ region, data, sitePathname, fullUrl, locale }) => {
    const { userDetails } = useUserDetails()
    const isLoggedIn = !!userDetails?.id

    const { t } = useTranslation()

    const head: HeadTransport = {
        title: t('directoryPageTitle'),
        description: t('directoryPageDescription'),
        copyright: 'Traum-Ferienwohnungen GmbH',
        googleSiteVerification: config.googleSiteVerification,
        locale: locale,
        canonical: {
            noIndex: false,
            noFollow: false,
            relNext: null,
            relPrev: null,
            url: fullUrl,
        },
    }

    return data.data !== null ? (
        <Page head={head}>
            <DirectoryPage
                isLoggedIn={isLoggedIn}
                firstName={userDetails?.firstName}
                headerSection={data.data.staticData.headerSection}
                sitePathname={sitePathname}
                region={region}
                directoryItems={data.data.sections}
                footerSection={data.data.staticData.footerSection}
            />
        </Page>
    ) : (
        <div>Data is not available.</div>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req, res }) => {
    const { user, device } = res.locals as ResponseLocals
    const { region } = req.params
    const sitePathname = req.path

    const requestUserConfig = deleteSession(user)

    const data = await pageClient.getDirectoryPage({ user, region })

    if (data.status === 404) {
        return {
            notFound: true,
        }
    }

    const protocol = req.header('x-forwarded-proto') || req.protocol || 'http'
    const host = req.headers['host']
    const fullUrl = `${protocol}://${host}${sitePathname}`

    const locale = getLocaleFromLanguage(user.language)

    return {
        props: {
            region,
            data,
            device,
            requestUserConfig: requestUserConfig,
            messages: await getMessagesFromLanguage(user.language),
            routes: await getRoutesFromDomain(user.domain),
            sitePathname,
            fullUrl,
            locale,
        },
    }
}

export default memo(DirectoryPageContainer)
